import React, { useState, useEffect } from 'react';
import { FileText, Plus, Check, AlertTriangle, History, Upload, ShoppingCart, X } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../App';
import { toast, Toaster } from 'sonner';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import { isNative } from '../utils/capacitor';
import { useTranslation } from 'react-i18next';
import DoctorCreateForm from '../components/prescription/DoctorCreateForm';
import ReviewSuggestions from '../components/prescription/ReviewSuggestions';
import PatientHealthInfo from '../components/prescription/PatientHealthInfo';
import UploadPrescription from '../components/prescription/UploadPrescription';
import MedicineOrderView from '../components/prescription/MedicineOrderView';
import PrescriptionHistory from '../components/prescription/PrescriptionHistory';
import { Card } from '@/components/ui/card';

const AIPrescription = ({ user, setUser }) => {
  const { t } = useTranslation();
  const isDoctor = user.role === 'doctor' || user.role === 'super_admin';
  const [activeTab, setActiveTab] = useState(isDoctor ? 'create' : 'patient-info');
  const [loading, setLoading] = useState(false);
  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [currentSuggestion, setCurrentSuggestion] = useState(null);

  // Patient Info State
  const [patientName, setPatientName] = useState('');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [gender, setGender] = useState('Male');
  const [allergies, setAllergies] = useState('');
  const [currentMedications, setCurrentMedications] = useState('');
  const [medicalHistory, setMedicalHistory] = useState('');

  // Diagnosis State
  const [symptoms, setSymptoms] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [severity, setSeverity] = useState('moderate');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const [patientSymptoms, setPatientSymptoms] = useState('');
  const [patientSuggestion, setPatientSuggestion] = useState(null);
  
  // Upload Prescription State
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadPatientName, setUploadPatientName] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedPrescriptions, setUploadedPrescriptions] = useState([]);
  const [selectedUploadedPrescription, setSelectedUploadedPrescription] = useState(null);
  
  // Camera State
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  
  // Manual medicine entry
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualMedicines, setManualMedicines] = useState([{ name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
  
  // Medicine Ordering
  const [medicineLinks, setMedicineLinks] = useState([]);
  const [orderingFrom, setOrderingFrom] = useState(null);

  useEffect(() => {
    if (!isDoctor && activeTab === 'history') {
      fetchPatientPrescriptions();
    }
  }, [isDoctor, activeTab]);

  useEffect(() => {
    if (activeTab === 'history') {
      if (isDoctor) { fetchHistory(); } else { fetchPatientPrescriptions(); fetchUploadedPrescriptions(); }
    }
  }, [activeTab, isDoctor]);

  const handleGenerateSuggestions = async () => {
    if (!patientName || !age || !symptoms || !diagnosis) { toast.error('Please fill in all required fields'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/prescription/suggest`, {
        patient_info: { patient_name: patientName, age: parseInt(age), weight: weight ? parseFloat(weight) : null, gender, allergies: allergies ? allergies.split(',').map(a => a.trim()) : [], current_medications: currentMedications ? currentMedications.split(',').map(m => m.trim()) : [], medical_history: medicalHistory ? medicalHistory.split(',').map(h => h.trim()) : [] },
        diagnosis: { symptoms: symptoms.split(',').map(s => s.trim()), diagnosis, severity, additional_notes: additionalNotes }
      });
      setCurrentSuggestion(response.data.prescription);
      toast.success('AI prescription suggestions generated!');
      setActiveTab('review');
    } catch (error) {
      toast.error('Failed to generate suggestions: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const handleApprovePrescription = async () => {
    if (!currentSuggestion) return;
    setLoading(true);
    try {
      await axios.post(`${API}/prescription/approve`, { prescription_id: currentSuggestion.id, approved_medications: currentSuggestion.suggested_medications, doctor_notes: '', generate_pdf: true });
      toast.success('Prescription approved successfully!');
      resetForm(); setActiveTab('create'); fetchHistory();
    } catch (error) { toast.error('Failed to approve prescription'); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/prescription/history?limit=20`); setPrescriptionHistory(r.data.prescriptions || []); }
    catch (error) { toast.error('Failed to load prescription history'); }
    finally { setLoading(false); }
  };

  const fetchPatientPrescriptions = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/prescription/patient/my-prescriptions?limit=20`); setPrescriptionHistory(r.data.prescriptions || []); }
    catch (error) { toast.error('Failed to load your prescriptions'); }
    finally { setLoading(false); }
  };

  const handlePatientAISuggestion = async () => {
    if (!patientSymptoms.trim()) { toast.error('Please describe your symptoms'); return; }
    setLoading(true);
    try { const r = await axios.post(`${API}/prescription/patient/ai-suggest?symptoms=${encodeURIComponent(patientSymptoms)}`); setPatientSuggestion(r.data.suggestion); toast.success('AI health information generated!'); }
    catch (error) { toast.error('Failed to get health information'); }
    finally { setLoading(false); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(file.type)) { toast.error('Only JPG, PNG, and PDF files are allowed'); return; }
      setUploadedFile(file); setCapturedImage(null);
    }
  };

  const startCamera = async () => {
    if (isNative()) {
      try {
        const image = await CapacitorCamera.getPhoto({ quality: 90, allowEditing: false, resultType: CameraResultType.Uri, source: CameraSource.Camera });
        const response = await fetch(image.webPath); const blob = await response.blob();
        setUploadedFile(new File([blob], `prescription_${Date.now()}.jpg`, { type: 'image/jpeg' }));
        setCapturedImage(image.webPath); toast.success('Photo captured successfully!');
      } catch (error) { if (error.message !== 'User cancelled photos app') toast.error('Could not access camera.'); }
    } else {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        setStream(mediaStream); setShowCamera(true);
        setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = mediaStream; }, 100);
      } catch (error) { toast.error('Could not access camera.'); }
    }
  };

  const stopCamera = () => { if (stream) { stream.getTracks().forEach(track => track.stop()); setStream(null); } setShowCamera(false); };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current; const canvas = canvasRef.current;
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        setUploadedFile(new File([blob], `prescription_${Date.now()}.jpg`, { type: 'image/jpeg' }));
        setCapturedImage(URL.createObjectURL(blob)); stopCamera(); toast.success('Photo captured!');
      }, 'image/jpeg', 0.9);
    }
  };

  const retakePhoto = () => { setCapturedImage(null); setUploadedFile(null); if (!isNative()) startCamera(); };

  const handleUploadPrescription = async () => {
    if (!uploadedFile || !uploadPatientName.trim()) { toast.error('Please select a file and enter patient name'); return; }
    setUploadLoading(true);
    try {
      const formData = new FormData(); formData.append('file', uploadedFile); formData.append('patient_name', uploadPatientName);
      if (uploadNotes) formData.append('notes', uploadNotes);
      const r = await axios.post(`${API}/prescription/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Prescription uploaded and processed!');
      setSelectedUploadedPrescription(r.data.prescription); setUploadedFile(null); setUploadPatientName(''); setUploadNotes(''); fetchUploadedPrescriptions();
    } catch (error) { toast.error('Failed to upload: ' + (error.response?.data?.detail || error.message)); }
    finally { setUploadLoading(false); }
  };

  const fetchUploadedPrescriptions = async () => {
    try { const r = await axios.get(`${API}/prescription/uploaded/my-uploads?limit=20`); setUploadedPrescriptions(r.data.prescriptions || []); } catch {}
  };

  const fetchMedicineLinks = async (prescriptionId, source) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/prescription/${prescriptionId}/order-medicines?source=${source}`);
      setMedicineLinks(r.data.medicines || []); setOrderingFrom({ type: source, id: prescriptionId });
      toast.success(`Found ${r.data.medicines?.length || 0} medicines`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate medicine links'); }
    finally { setLoading(false); }
  };

  const addManualMedicine = () => setManualMedicines([...manualMedicines, { name: '', dosage: '', frequency: '', duration: '', instructions: '' }]);
  const updateManualMedicine = (index, field, value) => { const u = [...manualMedicines]; u[index][field] = value; setManualMedicines(u); };
  const removeManualMedicine = (index) => setManualMedicines(manualMedicines.filter((_, i) => i !== index));

  const generateLinksFromManualEntry = async () => {
    const names = manualMedicines.filter(m => m.name.trim()).map(m => m.name);
    if (names.length === 0) { toast.error('Please enter at least one medicine name'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/prescription/medicines/order-links`, names);
      setMedicineLinks(r.data.medicines || []); setOrderingFrom({ type: 'manual', id: null });
      toast.success(`Found ${r.data.medicines?.length || 0} medicines`); setShowManualEntry(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to generate links'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setPatientName(''); setAge(''); setWeight(''); setGender('Male'); setAllergies('');
    setCurrentMedications(''); setMedicalHistory(''); setSymptoms(''); setDiagnosis('');
    setSeverity('moderate'); setAdditionalNotes(''); setCurrentSuggestion(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
            <FileText className="w-10 h-10 text-blue-600" />
            {isDoctor ? 'AI Prescription Assistant' : 'Health Information & Prescriptions'}
          </h1>
          <p className="text-gray-600 mt-2">{isDoctor ? 'AI-powered medication suggestions for your patients' : 'View your prescriptions and get general health information'}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="prescription-tabs">
          {isDoctor ? (
            <>
              <Button onClick={() => setActiveTab('create')} variant={activeTab === 'create' ? 'default' : 'outline'}><Plus className="w-4 h-4 mr-2" />New Prescription</Button>
              <Button onClick={() => setActiveTab('review')} variant={activeTab === 'review' ? 'default' : 'outline'} disabled={!currentSuggestion}><Check className="w-4 h-4 mr-2" />Review Suggestions</Button>
              <Button onClick={() => setActiveTab('history')} variant={activeTab === 'history' ? 'default' : 'outline'}><History className="w-4 h-4 mr-2" />History</Button>
            </>
          ) : (
            <>
              <Button onClick={() => setActiveTab('patient-info')} variant={activeTab === 'patient-info' ? 'default' : 'outline'}><AlertTriangle className="w-4 h-4 mr-2" />Health Info</Button>
              <Button onClick={() => { setActiveTab('upload'); fetchUploadedPrescriptions(); }} variant={activeTab === 'upload' ? 'default' : 'outline'}><Upload className="w-4 h-4 mr-2" />Upload Prescription</Button>
              <Button onClick={() => setActiveTab('history')} variant={activeTab === 'history' ? 'default' : 'outline'}><History className="w-4 h-4 mr-2" />My Prescriptions</Button>
            </>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'create' && isDoctor && (
          <DoctorCreateForm {...{ patientName, setPatientName, age, setAge, weight, setWeight, gender, setGender, allergies, setAllergies, currentMedications, setCurrentMedications, medicalHistory, setMedicalHistory, symptoms, setSymptoms, diagnosis, setDiagnosis, severity, setSeverity, additionalNotes, setAdditionalNotes, loading, handleGenerateSuggestions, t }} />
        )}

        {activeTab === 'review' && currentSuggestion && (
          <ReviewSuggestions {...{ currentSuggestion, loading, handleApprovePrescription, fetchMedicineLinks, setActiveTab }} />
        )}

        {activeTab === 'patient-info' && !isDoctor && (
          <PatientHealthInfo {...{ patientSymptoms, setPatientSymptoms, loading, handlePatientAISuggestion, patientSuggestion }} />
        )}

        {/* Prescription Details Modal */}
        {selectedUploadedPrescription && (
          <Card className="p-6 mb-6 bg-green-50 border-green-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-green-800">Prescription Details</h3>
              <Button onClick={() => setSelectedUploadedPrescription(null)} variant="outline" size="sm"><X className="w-4 h-4 mr-1" />Close</Button>
            </div>
            <div className="mb-4 p-3 bg-white border border-gray-200 rounded">
              <p className="font-semibold text-gray-800 mb-2">{selectedUploadedPrescription.patient_name}</p>
              <p className="text-sm text-gray-600">Status: <span className={`font-semibold ${selectedUploadedPrescription.extraction_status === 'completed' ? 'text-green-600' : selectedUploadedPrescription.extraction_status === 'failed' ? 'text-red-600' : 'text-yellow-600'}`}>{selectedUploadedPrescription.extraction_status}</span></p>
              <p className="text-sm text-gray-600">Uploaded: {new Date(selectedUploadedPrescription.uploaded_at).toLocaleString()}</p>
            </div>
            {selectedUploadedPrescription.extracted_medicines?.length > 0 && (
              <div className="space-y-3 mb-4">
                <h4 className="font-semibold text-gray-800">Extracted Medicines:</h4>
                {selectedUploadedPrescription.extracted_medicines.map((med, idx) => (
                  <div key={`em-${idx}`} className={`p-3 rounded border ${med.name.includes('manually') || med.name.includes('failed') ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
                    <p className="font-bold text-gray-800">{med.name}</p>
                    {med.dosage && med.dosage !== 'N/A' && <p className="text-sm text-gray-600">Dosage: {med.dosage}</p>}
                    {med.frequency && med.frequency !== 'N/A' && <p className="text-sm text-gray-600">Frequency: {med.frequency}</p>}
                  </div>
                ))}
              </div>
            )}
            {selectedUploadedPrescription.extraction_status === 'completed' && selectedUploadedPrescription.extracted_medicines?.some(m => !m.name.includes('manually') && !m.name.includes('failed')) && (
              <Button onClick={() => fetchMedicineLinks(selectedUploadedPrescription.id, 'uploaded')} className="bg-green-600 hover:bg-green-700"><ShoppingCart className="w-4 h-4 mr-2" />Order These Medicines</Button>
            )}
          </Card>
        )}

        {activeTab === 'upload' && !isDoctor && (
          <UploadPrescription {...{ uploadedFile, uploadPatientName, setUploadPatientName, uploadNotes, setUploadNotes, uploadLoading, handleUploadPrescription, handleFileUpload, showCamera, startCamera, stopCamera, capturePhoto, retakePhoto, videoRef, canvasRef, capturedImage, showManualEntry, setShowManualEntry, manualMedicines, addManualMedicine, updateManualMedicine, removeManualMedicine, generateLinksFromManualEntry, loading, uploadedPrescriptions, setSelectedUploadedPrescription, fetchMedicineLinks }} />
        )}

        <MedicineOrderView orderingFrom={orderingFrom} medicineLinks={medicineLinks} onClose={() => { setOrderingFrom(null); setMedicineLinks([]); }} />

        {activeTab === 'history' && (
          <PrescriptionHistory {...{ isDoctor, loading, prescriptionHistory, fetchMedicineLinks, uploadedPrescriptions, setSelectedUploadedPrescription, setActiveTab, selectedUploadedPrescription }} />
        )}
      </div>
    </div>
  );
};

export default AIPrescription;
